import type { TripCreateRequest } from "@mtform/shared";
import {
  formatIsoDateForDisplay,
  formatTimeForDisplay,
  joinAddressLine,
} from "@/lib/format";

// =============================================================================
// TripSummary
//
// Shared sectioned read-out of the wire payload used on the final review
// step. The wizard's collapsed cards above it already give a quick scan;
// this is the more detailed pass — pickup time, appointment buffer hint,
// round-trip leg, multi-leg count, etc — without having to expand each
// step individually.
// =============================================================================

const TRANSPORT_LABELS: Record<string, string> = {
  AMB: "Ambulatory",
  "W/C": "Wheelchair",
  BWC: "Bariatric Wheelchair",
  STR: "Stretcher",
  BSTR: "Bariatric Stretcher",
};

const ORDER_LABELS: Record<string, string> = {
  one_time: "One-Time",
  standing_order: "Standing Order",
};

const TRIP_LABELS: Record<string, string> = {
  one_way: "One Way",
  round_trip: "Round Trip",
  multi_leg: "Multi-Leg",
};

interface TripSummaryProps {
  payload: TripCreateRequest;
}

export function TripSummary({ payload }: TripSummaryProps) {
  return (
    <div className="grid gap-3 text-sm sm:grid-cols-2">
      <SummarySection title="Patient" className="sm:col-span-2">
        <p>
          <strong className="font-bold">
            {[payload.firstName, payload.lastName].filter(Boolean).join(" ")}
          </strong>
          {" — MRN: "} {payload.mrn}
        </p>
        {payload.dob ? <p>DOB: {formatIsoDateForDisplay(payload.dob)}</p> : null}
        {payload.department ? <p>Department: {payload.department}</p> : null}
        {payload.costCenter ? <p>Cost Center: {payload.costCenter}</p> : null}
      </SummarySection>

      <SummarySection title="Transport">
        <p>Type: {TRANSPORT_LABELS[payload.transportType] ?? payload.transportType}</p>
        <p>
          {ORDER_LABELS[payload.orderType] ?? payload.orderType} /{" "}
          {TRIP_LABELS[payload.tripType] ?? payload.tripType}
        </p>
        {/* Surface companions + wheelchair-source if the form captured
            them. schema.ts folds these into the wire `pickupNotes` so
            triplink dispatch sees them inline; we re-extract here so
            the requester's review screen calls each one out as its
            own line instead of burying it in the notes blob. */}
        {(() => {
          const label = extractCompanionLabel(payload.pickupNotes);
          return label ? <p>Companions: {label}</p> : null;
        })()}
        {(() => {
          const label = extractWheelchairLabel(payload.pickupNotes);
          return label ? <p>Wheelchair: {label}</p> : null;
        })()}
      </SummarySection>

      <SummarySection title="Schedule">
        {payload.transportDate ? (
          <p>Date: {formatIsoDateForDisplay(payload.transportDate)}</p>
        ) : null}
        {payload.timeType === "will_call" ? (
          <p>Scheduling: Will Call</p>
        ) : (
          (() => {
            const value = payload.pickupTimeEstimated || payload.appointmentTime;
            const label = payload.timeType === "pickup" ? "Pickup Time" : "Appointment Time";
            // Single template-literal child so screen readers don't read
            // a phantom space between the label and the colon.
            return value && value !== "WILL_CALL"
              ? <p>{`${label}: ${formatTimeForDisplay(value)}`}</p>
              : null;
          })()
        )}
        {payload.appointmentBufferMinutes &&
        payload.appointmentTime &&
        payload.appointmentTime !== "WILL_CALL" ? (
          <p className="text-muted-foreground">
            Patient on campus 15 minutes early.
          </p>
        ) : null}
      </SummarySection>

      <SummarySection title="Route" className="sm:col-span-2">
        <p className="route-stop route-stop--from">From: {joinAddressLine(payload.pickupAddress, payload.pickupCityZip)}</p>
        {payload.pickupLocation ? <p className="route-meta">Location: {payload.pickupLocation}</p> : null}
        <p className="route-stop route-stop--to">
          To:{" "}
          {joinAddressLine(payload.destinationAddress, payload.destinationCityZip)}
        </p>
        {payload.tripType === "round_trip" ? (
          <RoundTripSummary payload={payload} />
        ) : null}
        {payload.tripType === "multi_leg" && payload.additionalLegs.length > 0 ? (
          <p>{payload.additionalLegs.length} additional leg(s)</p>
        ) : null}
      </SummarySection>

      <SummarySection title="Requester" className="sm:col-span-2">
        <p>
          {payload.requestedBy} — {payload.callbackPhone}
        </p>
        {payload.requesterEmail ? <p>Email: {payload.requesterEmail}</p> : null}
      </SummarySection>
    </div>
  );
}

function SummarySection({
  title,
  className,
  children,
}: {
  title: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`rounded-xl border border-border/70 bg-foreground/[0.02] px-4 py-3.5 ${className ?? ""}`}
    >
      <h3 className="text-[0.68rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
        {title}
      </h3>
      <div className="mt-2 grid gap-1.5 text-sm leading-snug text-foreground/90">{children}</div>
    </section>
  );
}

/** Pull the "Companions: X traveling with patient" line out of
 *  pickupNotes (the wire payload's free-text field). schema.ts
 *  prepends it at conversion time so the summary can recover the
 *  count without needing a separate wire schema field.
 *
 *  Boundary covers both segment positions: start of string, or after
 *  the " · " separator schema.ts uses to join dispatch flags. */
function extractCompanionLabel(pickupNotes: string): string {
  const m = /(?:^| · )Companions:\s+(.+?)\s+traveling with patient/u.exec(
    pickupNotes ?? "",
  );
  return m ? m[1]! : "";
}

/** Pull "Wheelchair: ..." out of pickupNotes for the same reason.
 *  Returns a human-friendly label, or "" if not captured. */
function extractWheelchairLabel(pickupNotes: string): string {
  const notes = pickupNotes ?? "";
  if (/(?:^| · )Wheelchair: patient brings their own/u.test(notes))
    return "Patient brings their own";
  if (/(?:^| · )Wheelchair: dispatch provides/u.test(notes))
    return "Dispatch provides";
  return "";
}

function RoundTripSummary({ payload }: { payload: TripCreateRequest }) {
  if (payload.returnPickupTime === "WILL_CALL") return <p>Return: Will Call</p>;
  if (!payload.returnPickupTime) return null;
  const display = formatTimeForDisplay(payload.returnPickupTime);
  if (payload.returnToOriginalPickup) {
    return <p>Return at {display} → Original pick-up</p>;
  }
  return (
    <p>
      Return at {display} → {payload.returnCustomDestinationAddress}
    </p>
  );
}
